// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { ErrorService } from '@services/error.service';
import { map, Observable } from 'rxjs';
import { ApiService } from '@services/api.service';
import { UserLogonState } from '@interfaces/user.interfaces';
import { MatDialogRef } from '@angular/material/dialog';
import { ErrorModalComponent } from 'src/app/modals/error-modal/error-modal.component';
import { SettingsService } from '@services/settings.service';
import { AuthenticationResponse, AuthenticationStatus } from '@interfaces/auth.interfaces';

@Injectable({
    providedIn: 'root',
})
export class LoginService {
    private _email = '';
    private _resetToken = '';
    private _tempPassword: string = null;
    private _username = '';

    constructor(
        private readonly apiService: ApiService,
        private readonly authService: AuthService,
        private readonly errorService: ErrorService,
        private readonly settingsService: SettingsService
    ) {}

    public getEmail(): string {
        return this._email;
    }

    public setEmail(email: string): void {
        this._email = email;
    }

    public getResetToken() {
        return this._resetToken;
    }

    public setResetToken(resetToken: string) {
        this._resetToken = resetToken;
    }

    public getTempPassword() {
        return this._tempPassword;
    }

    public setTempPassword(password: string) {
        this._tempPassword = password;
    }

    public getUsername(): string {
        return this._username;
    }

    public setUsername(username: string) {
        this._username = username;
    }

    authenticateUser(
        email: string,
        password: string,
        mfaDevice: string | null,
        successCallback = (response: AuthenticationResponse) => {},
        failureCallback = () => {},
        mfaCallback = (state: AuthenticationStatus) => {},
        newPasswordRequiredCallback = undefined
    ): void {
        this.authService.authenticateUser(
            email,
            password,
            (response) => {
                this._username = '';
                successCallback(response);
            },
            () => {
                failureCallback();
            },
            (state) => {
                mfaCallback(state);
            },
            newPasswordRequiredCallback
        );
    }

    public logLoginState(
        userId: number,
        logonId: string,
        state: UserLogonState,
        logonMessage?: string,
        logonType?: 'Logon' | 'MFA',
        mfaDevice?: string
    ): Observable<boolean> {
        return this.apiService
            .postV2$<boolean>('api/user/log/auth', {
                userId,
                logonId,
                logonType,
                userLogonState: state,
                MFADevice: mfaDevice,
                userLogonMessage: logonMessage,
            })
            .pipe(map((response) => response?.data));
    }

    async showTerminatedDealershipDialog(oemCode: string = null): Promise<MatDialogRef<ErrorModalComponent, void>> {
        const { supportEmail: email, supportPhone: phone } = await this.settingsService.getOemSettings(oemCode);

        return this.errorService.showErrorAtService({
            title: 'Dealership Not Active',
            message: `This dealership is currently not active on the application. Please contact the support team at ${email} or ${phone} for additional information and assistance.`,
        });
    }
}
